import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createPageUrl } from '@/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
// PERF: keep Landing bundle light (tracking modules are loaded only on interaction).
import { lazySendMetaEvent as sendMetaEvent, lazyTrackEvent as trackEvent } from '@/lib/lazyTracking';
import BrandLink from '@/components/ui/BrandLink';
import Tagline from '@/components/ui/Tagline';
import CookieSettingsLink from '@/components/cookies/CookieSettingsLink';
import UgcFacebookReel from '@/components/marketing/UgcFacebookReel';
import dashboardShot from '@/assets/landing/screens/dashboard.webp';
import echeancesShot from '@/assets/landing/screens/echeances.webp';
import enveloppesShot from '@/assets/landing/screens/enveloppes.webp';
import caShot from '@/assets/landing/screens/ca.webp';
import depensesShot from '@/assets/landing/screens/depenses.webp';
import remunerationShot from '@/assets/landing/screens/remuneration.webp';
import facturesShot from '@/assets/landing/screens/factures.webp';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Logo from '@/components/ui/Logo';
import { Calculator, Bell, PiggyBank, FileText, Shield, Zap, Check, ArrowRight, Sparkles, Crown, TrendingUp, Calendar, Users, Menu, X, AlertCircle, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { PROGRAMMATIC_PAGES } from '@/lib/programmaticSeo';
import SeoJsonLd from '@/components/seo/SeoJsonLd';

const FACEBOOK_PAGE_URL = 'https://www.facebook.com/profile.php?id=61585414149400';

function FacebookIcon({ className = 'w-5 h-5' }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
    >
      <path d="M22 12.06C22 6.51 17.52 2 11.96 2S2 6.51 2 12.06C2 17.08 5.66 21.23 10.44 22v-7.03H7.9v-2.91h2.54V9.85c0-2.5 1.49-3.88 3.77-3.88 1.09 0 2.23.2 2.23.2v2.46h-1.25c-1.23 0-1.61.76-1.61 1.54v1.85h2.74l-.44 2.91h-2.3V22C18.34 21.23 22 17.08 22 12.06z" />
    </svg>
  );
}

const TRIAL_FLOW_URL = `/register?next=${encodeURIComponent('/onboarding?next=/billing')}`;
const PRO_TRIAL_NO_CARD_URL = "https://tresopilot.fr/a/A56F6B1018";


const landingSeoGraph = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'TrésoPilot',
    url: 'https://tresopilot.fr',
    logo: 'https://tresopilot.fr/android-chrome-512x512.png',
    email: 'contact@tresopilot.fr',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'TrésoPilot',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: 'https://tresopilot.fr',
    description: 'Application web de trésorerie pour micro-entrepreneurs : suivi du chiffre d’affaires encaissé, vrai net, provisions et échéances.',
    offers: [
      {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'EUR',
        name: 'Starter',
      },
      {
        '@type': 'Offer',
        price: '6.99',
        priceCurrency: 'EUR',
        name: 'Plus',
      },
      {
        '@type': 'Offer',
        price: '12.99',
        priceCurrency: 'EUR',
        name: 'Pro',
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'TrésoPilot est-il conforme URSSAF/TVA ?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'TrésoPilot est un outil d’aide à la gestion qui fournit des estimations et des rappels. Vous restez responsable de vos déclarations officielles et devez vérifier les montants sur les portails officiels.',
        },
      },
      {
        '@type': 'Question',
        name: 'Pourquoi payer si je peux utiliser Excel ?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'TrésoPilot automatise les calculs, centralise les échéances, envoie des rappels et conserve votre historique, ce qui réduit la charge mentale et le risque d’oubli.',
        },
      },
      {
        '@type': 'Question',
        name: 'Le paiement est-il sécurisé ?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Oui. Les paiements sont gérés par Stripe et les données bancaires ne sont pas stockées par TrésoPilot.',
        },
      },
      {
        '@type': 'Question',
        name: 'Puis-je annuler mon abonnement quand je veux ?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Oui, vous pouvez annuler à tout moment sans frais et garder l’accès à vos données en mode gratuit.',
        },
      },
    ],
  },
];

const features = [
  {
    icon: TrendingUp,
    title: "Dashboard & vrai net",
    description: "Votre CA encaissé, vos provisions et votre “reste” en un coup d'œil (TTC si TVA)."
  },
  {
    icon: Calculator,
    title: "Provisions automatiques",
    description: "URSSAF, impôts, TVA… estimations basées sur vos paramètres et votre activité."
  },
  {
    icon: Calendar,
    title: "Échéancier personnalisé",
    description: "Toutes vos dates clés (URSSAF, TVA, CFE…) regroupées au même endroit."
  },
  {
    icon: Bell,
    title: "Rappels intelligents",
    description: "Alertes in-app + emails (selon l’offre) pour ne plus subir les mauvaises surprises."
  },
  {
    icon: PiggyBank,
    title: "Enveloppes virtuelles",
    description: "Saisissez votre CA → TrésoPilot vous dit combien mettre de côté chaque semaine."
  },
  {
    icon: FileText,
    title: "Devis, factures & exports",
    description: "Documents PDF propres + préparation Factur-X (EN16931) + exports CSV pour banque/compta (selon l’offre)."
  }
];

const plans = [
  {
    id: 'free',
    name: 'Starter',
    price: { monthly: 0, yearly: 0 },
    description: 'Pour découvrir et commencer',
    features: [
      'Tableau de bord clair (CA TTC si TVA)',
      'Provisions URSSAF + impôts automatiques',
      'Échéancier (URSSAF, impôt, CFE, TVA si applicable)',
      'Objectifs & suivi du “vrai net”',
      '1 activité',
      'Historique 3 mois'
    ],
    limitations: [
      'Pas d\'enveloppes virtuelles',
      'Pas d\'exports PDF/CSV',
      'Pas de rappels email',
      'Pas de multi-activités',
      'Pas de simulations',
      'Pas de rapports'
    ],
    cta: 'Commencer gratuitement',
    variant: 'outline'
  },
  {
    id: 'plus',
    name: 'Plus',
    price: { monthly: 6.99, yearly: 69 },
    description: 'Pour ne plus rater une échéance',
    popular: true,
    features: [
      'Tout du Starter',
      'Historique illimité',
      'Enveloppes virtuelles (TVA, URSSAF, impôts, salaire)',
      'Rappels in-app + emails',
      'Exports PDF/CSV (banque, compta)',
      'Livre des recettes (registre encaissements)',
      'Support prioritaire'
    ],
    limitations: [
      'Pas de multi-activités',
      'Pas de prévisionnel avancé',
      'Pas de rapports & IA premium'
    ],
    cta: 'Essayer Plus',
    variant: 'default',
    highlighted: true
  },
  {
    id: 'pro',
    name: 'Pro',
    price: { monthly: 12.99, yearly: 129 },
    description: 'Pour piloter comme une vraie boîte',
    features: [
      'Tout de Plus',
      'Multi-activités illimité',
      'Factures & Clients + Factur-X prêt (CA encaissé TTC)',
      'Charges déductibles + CFE',
      'Prévisionnel de trésorerie',
      'Alertes seuils TVA',
      'Simulateur salaire / rémunération',
      'Rapports & recommandations IA'
    ],
    cta: 'Passer Pro',
    variant: 'default'
  }
];

const productTour = [
  {
    kicker: "Cockpit",
    title: "Vous savez où vous en êtes — sans tableur",
    description: "CA encaissé (TTC si TVA), provisions et vrai net : tout est déjà calculé pour éviter les surprises.",
    bullets: [
      "Vision claire du mois + du trimestre",
      "Prochaine échéance mise en avant",
      "Conseils immédiats pour piloter",
    ],
    urgency: "⏱️ Chaque jour sans visibilité, vous risquez de vous payer trop tôt (ou pas assez).",
    ctaPrimaryLabel: "Construire mon cockpit maintenant",
    ctaPrimaryHref: TRIAL_FLOW_URL,
    ctaSecondaryLabel: "Démarrer mon essai Pro 7 jours",
    ctaSecondaryHref: TRIAL_FLOW_URL,
    image: dashboardShot,
    alt: "Aperçu du dashboard TrésoPilot",
  },
  {
    kicker: "Échéances",
    title: "Vos dates URSSAF / TVA au même endroit",
    description: "Un échéancier personnalisé : vous voyez ce qui arrive, quand, et combien mettre de côté.",
    bullets: [
      "URSSAF, TVA, CFE… regroupés",
      "Compte à rebours avant chaque deadline",
      "Rappels in‑app + emails selon l'offre",
    ],
    urgency: " La prochaine échéance n'attend pas : anticipez avant la dernière semaine.",
    ctaPrimaryLabel: "Générer mon échéancier en 2 min",
    ctaPrimaryHref: TRIAL_FLOW_URL,
    ctaSecondaryLabel: "Activer l'essai Pro (rappels email)",
    ctaSecondaryHref: TRIAL_FLOW_URL,
    image: echeancesShot,
    alt: "Aperçu des échéances URSSAF et TVA",
  },
  {
    kicker: "Enveloppes",
    title: "Vous provisionnez chaque semaine, automatiquement",
    description: "Fin du stress “à la fin du trimestre”. TrésoPilot transforme vos charges en un virement hebdo simple.",
    bullets: [
      "Objectifs par enveloppe (URSSAF, TVA, impôts…)",
      "Virement hebdo suggéré",
      "Mise à jour rapide en 1 clic",
    ],
    urgency: "🔥 Stop au “c'est passé où mon argent ?” : lissez vos charges avant qu'elles tombent.",
    ctaPrimaryLabel: "Lisser mes charges dès aujourd'hui",
    ctaPrimaryHref: TRIAL_FLOW_URL,
    ctaSecondaryLabel: "Démarrer l'essai Pro 7 jours",
    ctaSecondaryHref: TRIAL_FLOW_URL,
    image: enveloppesShot,
    alt: "Aperçu des enveloppes de charges",
  },
  {
    kicker: "Chiffre d'affaires",
    title: "Vous pilotez sur l'argent réellement encaissé",
    description: "Le CA TTC (si TVA) est pris en compte : c'est ce qui entre sur votre compte, et ce qui finance vos charges.",
    bullets: [
      "Historique + filtres par activité",
      "À côté / net calculés automatiquement",
      "Alertes seuils TVA (Pro)",
    ],
    urgency: "📉 Un mois “bon” peut cacher une grosse TVA : voyez le TTC, le net, et le “à côté”.",
    ctaPrimaryLabel: "Voir mon vrai net (TTC) maintenant",
    ctaPrimaryHref: TRIAL_FLOW_URL,
    ctaSecondaryLabel: "Activer l'essai Pro 7 jours",
    ctaSecondaryHref: TRIAL_FLOW_URL,
    image: caShot,
    alt: "Aperçu de l'historique de chiffre d'affaires",
  },
  {
    kicker: "Charges",
    title: "Vos dépenses utiles, sans vous perdre",
    description: "Saisissez vos charges : TrésoPilot calcule le déductible et la TVA récupérable pour coller à la réalité.",
    bullets: [
      "Suivi des charges déductibles (Pro)",
      "TVA récupérable estimée",
      "Catégories claires",
    ],
    urgency: "💸 Chaque dépense non suivie = du net “fantôme” (et parfois de la TVA perdue).",
    ctaPrimaryLabel: "Suivre mes charges déductibles",
    ctaPrimaryHref: TRIAL_FLOW_URL,
    ctaSecondaryLabel: "Passer en Pro 7 jours (sans risque)",
    ctaSecondaryHref: TRIAL_FLOW_URL,
    image: depensesShot,
    alt: "Aperçu des charges déductibles",
  },
  {
    kicker: "Factures & Clients",
    title: "Factur-X prêt : vous avancez sans vous compliquer la vie",
    description: "Vous éditez vos factures comme d’habitude. TrésoPilot vous montre les champs manquants (vendeur/client) et génère votre Factur-X dès que tout est complet.",
    bullets: [
      "Badge ‘Champs manquants’ clair et actionnable",
      "Raccourcis pour compléter infos vendeur / vérifier le client",
      "Socle prêt pour l’envoi via PDP (ensuite)",
    ],
    urgency: " Facture électronique : prenez 10 minutes maintenant, évitez la panique quand ça devient obligatoire.",
    ctaPrimaryLabel: "Créer ma 1ʳᵉ facture Factur-X",
    ctaPrimaryHref: TRIAL_FLOW_URL,
    ctaSecondaryLabel: "Activer l’essai Pro 7 jours",
    ctaSecondaryHref: TRIAL_FLOW_URL,
    image: facturesShot,
    alt: "Aperçu des factures avec badge Factur-X",
  },
  {
    kicker: "Salaire safe",
    title: "Combien vous pouvez vous verser, maintenant ?",
    description: "Un simulateur qui tient compte de votre trésorerie et des échéances à venir, pour vous payer sans stress.",
    bullets: [
      "Choix de l'horizon (30/60/90 jours…)",
      "Coussin de sécurité configurable",
      "Historique des versements",
    ],
    urgency: " Mettez fin au doute : décidez de votre versement sans vous mettre en danger.",
    ctaPrimaryLabel: "Sécuriser mon versement",
    ctaPrimaryHref: TRIAL_FLOW_URL,
    ctaSecondaryLabel: "Démarrer l'essai Pro 7 jours",
    ctaSecondaryHref: TRIAL_FLOW_URL,
    image: remunerationShot,
    alt: "Aperçu du simulateur de rémunération",
  },
];

function formatPriceFr(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 }).format(n);
}

export default function Landing() {

  // Video demo (landing) — best-effort protection + PiP on scroll (after user interaction)
  const demoVideoRef = useRef(null);
  const demoVideoUserUnlockedRef = useRef(false);
  const demoVideoShellRef = useRef(null);
  const demoVideoCardRef = useRef(null);
  const [demoSticky, setDemoSticky] = useState(false);
  const [demoStickyPlaceholderH, setDemoStickyPlaceholderH] = useState(null);
  
// --- Mini simulateur (estimation indicative) ---
const [simCA, setSimCA] = useState("");
const [simHasTVA, setSimHasTVA] = useState(false);
const [simHasACRE, setSimHasACRE] = useState(false);
const [simActivity, setSimActivity] = useState('services_bic');
const [simHasVL, setSimHasVL] = useState(false);
const [simTVARate, setSimTVARate] = useState('0.20');
const [simTerritory, setSimTerritory] = useState('FR'); // FR, GP, MQ, GF, RE
const [simDomPeriod, setSimDomPeriod] = useState('cruise'); // p1, p2, cruise

const simCAValue = Number(String(simCA).replace(",", ".") || 0);
const ca = isFinite(simCAValue) ? simCAValue : 0;

// Hypothèses transparentes (pour un “ordre de grandeur”).
// Sources (à date) :
// - Métropole : taux micro-entrepreneur 2026 + principe ACRE.
// - DOM (Guadeloupe / Martinique / Guyane / Réunion) : taux spécifiques auto-entrepreneurs (3 périodes).
// - TVA DOM : 8,5% (Guadeloupe/Martinique/Réunion) ; pas de TVA en Guyane.
const TERRITORY_LABELS = {
  FR: 'France (métropole)',
  GP: 'Guadeloupe',
  MQ: 'Martinique',
  GF: 'Guyane',
  RE: 'Réunion',
};

const DOM_PERIOD_LABELS = {
  p1: "Période 1 (jusqu’à fin 7e trimestre)",
  p2: "Période 2 (jusqu’à fin 3e année)",
  cruise: "Régime de croisière (4e année+)",
};

const ACTIVITY_LABELS = {
  vente: 'Vente / hébergement',
  services_bic: 'Prestations de services (BIC)',
  liberal_bnc: 'Activité libérale (BNC)',
  cipav: 'Libérale CIPAV (BNC)',
};

// Métropole (2026)
const URSSAF_RATES = { vente: 12.3, services_bic: 21.2, liberal_bnc: 25.6, cipav: 23.2 };

// DOM (hors Mayotte) — taux réduits en début d’activité (appliqués automatiquement), par période.
// Source : autoentrepreneur.urssaf.fr (taux 2026).
const URSSAF_DOM_RATES = {
  p1: { vente: 2.1, services_bic: 3.6, liberal_bnc: 4.3, cipav: 7.8 },
  p2: { vente: 6.2, services_bic: 10.6, liberal_bnc: 12.8, cipav: 11.6 },
  cruise: { vente: 8.2, services_bic: 14.2, liberal_bnc: 17.1, cipav: 15.5 },
};

const VL_RATES = { vente: 1.0, services_bic: 1.7, liberal_bnc: 2.2, cipav: 2.2 };

const isDom = simTerritory !== 'FR';

const previewUrssafPct = (activityKey) => {
  const v = isDom
    ? (URSSAF_DOM_RATES[simDomPeriod]?.[activityKey] ?? URSSAF_RATES[activityKey])
    : URSSAF_RATES[activityKey];
  return Number(v || 0);
};

// URSSAF : taux selon activité (et territoire). ACRE ≈ réduction de 50% (mini-simulateur : uniquement en métropole).
const baseUrssafPct = isDom
  ? (URSSAF_DOM_RATES[simDomPeriod]?.[simActivity] ?? URSSAF_RATES[simActivity] ?? 21.2)
  : (URSSAF_RATES[simActivity] ?? 21.2);

const urssafPct = (!isDom && simHasACRE) ? (baseUrssafPct / 2) : baseUrssafPct;

// Impôt sur le revenu : par défaut NON inclus (trop dépendant du foyer).
// Si versement libératoire : on applique le taux correspondant à l’activité.
const vlPct = simHasVL ? (VL_RATES[simActivity] ?? 0) : 0;

// TVA : on suppose que le CA saisi est TTC si TVA (taux sélectionnable).
const tvaRate = simHasTVA ? Number(simTVARate || 0.20) : 0;
const tvaPct = Math.round(tvaRate * 10000) / 100;
const tvaDue = simHasTVA ? (ca - ca / (1 + tvaRate)) : 0;

const simUrssaf = ca * (urssafPct / 100);
const simVL = ca * (vlPct / 100);
const simTotal = simUrssaf + simVL + tvaDue;
const simRemaining = Math.max(0, ca - simTotal);

// Rémunération indicative (mini-simulateur) : on garde un coussin de sécurité fixe.
// NB : hors dépenses professionnelles et hors impôt sur le revenu (sauf versement libératoire).
const SIM_SAFETY_PCT = 0.15; // 15% de coussin
const simSafetyBuffer = simRemaining * SIM_SAFETY_PCT;
const simRemuneration = Math.max(0, simRemaining - simSafetyBuffer);

// Ajuste les defaults du mini-simulateur quand le territoire change.
useEffect(() => {
  try {
    const dom = simTerritory !== 'FR';

    if (dom) {
      // On n'applique pas l'ACRE ici : en DOM, le mini-simulateur utilise les taux spécifiques (3 périodes).
      if (simHasACRE) setSimHasACRE(false);

      // TVA : Guyane = pas de TVA ; GP/MQ/RE = taux normal local 8,5%.
      if (simTerritory === 'GF') {
        if (simHasTVA) setSimHasTVA(false);
        if (String(simTVARate) !== '0.00') setSimTVARate('0.00');
      } else {
        if (['0.20', '0.10', '0.055', '0.00'].includes(String(simTVARate))) setSimTVARate('0.085');
      }
    } else {
      // Retour métropole : repasse au taux de TVA “standard” si on venait du DOM.
      if (['0.085', '0.021', '0.0105', '0.00'].includes(String(simTVARate))) setSimTVARate('0.20');
    }
  } catch (_) {}
}, [simTerritory, simHasACRE, simHasTVA, simTVARate]);
// Meta Pixel + CAPI: ViewContent when pricing section is visible (useful for ads traffic)
  useEffect(() => {
    let timer = null;
    let tries = 0;

    try {
      const key = 'meta_viewcontent_landing_pricing_sent';
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(key)) return;

      const el = document.getElementById('pricing');
      if (!el) return;

      const attempt = async () => {
        tries += 1;
        if (tries > 60) {
          if (timer) clearInterval(timer);
          timer = null;
          return;
        }

        try {
          const eid = await sendMetaEvent({
            eventName: 'ViewContent',
            customData: { content_name: 'LandingPricing', content_category: 'pricing' },
            eventSourceUrl: window.location.href,
          });

          if (eid && typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(key, '1');
            if (timer) clearInterval(timer);
            timer = null;
          }
        } catch (_) {}
      };

      const obs = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry && entry.isIntersecting) {
            // Try immediately; if consent wasn't granted yet, retry until it is (max 60s).
            if (!timer) timer = setInterval(attempt, 1000);
            attempt();
          }
        },
        { threshold: 0.3 }
      );

      obs.observe(el);

      return () => {
        try {
          obs.disconnect();
          if (timer) clearInterval(timer);
        } catch (_) {}
      };
    } catch (_) {}

    return () => {
      if (timer) clearInterval(timer);
    };
  }, []);


// “PiP” auto au scroll (fiable) : on utilise un mini-lecteur in-page (type YouTube).
// Le PiP navigateur ne peut pas être forcé de façon répétée sur scroll (politiques de
// "user activation" très strictes) : on tente quand même en best-effort, mais on
// garantit une expérience PiP stable avec le mode "mini".
useEffect(() => {
  const video = demoVideoRef.current;
  const shell = demoVideoShellRef.current;
  const card = demoVideoCardRef.current;
  if (!video || !shell || !card || typeof window === 'undefined') return;

  let mounted = true;
  let isInView = true;
  let stickyOn = false;
  let lastPiPAttemptAt = 0;

  const markUnlocked = () => { demoVideoUserUnlockedRef.current = true; };

  // Best-effort: prevent easy "Download video" via the UI / context menu
  const preventContextMenu = (e) => { try { e.preventDefault(); } catch (_) {} };

  const supportsBrowserPiP = () => {
    try {
      return Boolean(document.pictureInPictureEnabled) && typeof video.requestPictureInPicture === 'function';
    } catch (_) {
      return false;
    }
  };

  const measurePlaceholder = () => {
    try {
      const rect = card.getBoundingClientRect();
      if (rect?.height && rect.height > 0) {
        setDemoStickyPlaceholderH(Math.ceil(rect.height));
      }
    } catch (_) {}
  };

  const setSticky = (on) => {
    if (!mounted) return;
    if (stickyOn === on) return;
    stickyOn = on;
    if (on) {
      measurePlaceholder();
      setDemoSticky(true);
    } else {
      setDemoSticky(false);
      setDemoStickyPlaceholderH(null);
    }
  };

  const tryEnterBrowserPiP = () => {
    try {
      if (!supportsBrowserPiP()) return;
      if (!demoVideoUserUnlockedRef.current) return;
      if (document.pictureInPictureElement) return;
      if (video.ended) return;
      if (video.readyState < 2) return;

      const now = Date.now();
      if (now - lastPiPAttemptAt < 600) return;
      lastPiPAttemptAt = now;

      const p = video.requestPictureInPicture?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (_) {}
  };

  const shouldStick = () => {
    try {
      return !isInView && !video.paused && !video.ended;
    } catch (_) {
      return false;
    }
  };

  const onPlay = () => {
    markUnlocked();
    if (shouldStick()) {
      setSticky(true);
      tryEnterBrowserPiP();
    }
  };

  const onPauseOrEnded = () => {
    setSticky(false);
  };

  const onEnterPiP = () => {
    // Si le PiP navigateur s'active, on désactive le mini-lecteur pour éviter doublon.
    setSticky(false);
  };

  const onLeavePiP = () => {
    // Si l'utilisateur est toujours loin de la vidéo et que ça joue, on garde le mode mini.
    if (shouldStick()) setSticky(true);
  };

  const onResize = () => {
    if (stickyOn) measurePlaceholder();
  };

  video.addEventListener('play', onPlay);
  video.addEventListener('pause', onPauseOrEnded);
  video.addEventListener('ended', onPauseOrEnded);
  video.addEventListener('pointerdown', markUnlocked);
  video.addEventListener('click', markUnlocked);
  video.addEventListener('contextmenu', preventContextMenu);
  video.addEventListener('enterpictureinpicture', onEnterPiP);
  video.addEventListener('leavepictureinpicture', onLeavePiP);
  window.addEventListener('resize', onResize);

  const obs = new IntersectionObserver(
    (entries) => {
      const entry = entries?.[0];
      if (!entry) return;

      const inView = entry.isIntersecting && (entry.intersectionRatio ?? 0) >= 0.2;
      isInView = inView;

      // Quand on revient sur la section vidéo : on ferme PiP navigateur (si ouvert) et on sort du mini.
      if (inView) {
        setSticky(false);
        if (document.pictureInPictureElement === video) {
          try { document.exitPictureInPicture?.(); } catch (_) {}
        }
        return;
      }

      // Quand on sort de la zone vidéo : si la vidéo est en lecture, on passe en mini (fiable)
      // et on tente PiP navigateur en best-effort.
      if (shouldStick()) {
        setSticky(true);
        tryEnterBrowserPiP();
      }
    },
    { threshold: [0, 0.2, 0.5, 1] }
  );

  try { obs.observe(shell); } catch (_) {}

  return () => {
    mounted = false;
    try {
      obs.disconnect();
      window.removeEventListener('resize', onResize);

      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPauseOrEnded);
      video.removeEventListener('ended', onPauseOrEnded);
      video.removeEventListener('pointerdown', markUnlocked);
      video.removeEventListener('click', markUnlocked);
      video.removeEventListener('contextmenu', preventContextMenu);
      video.removeEventListener('enterpictureinpicture', onEnterPiP);
      video.removeEventListener('leavepictureinpicture', onLeavePiP);
    } catch (_) {}
  };
}, []);

  const navigate = useNavigate();

  // Arrow function volontairement: évite toute ambiguïté de parsing lors de merges/edits.
  const handleLandingCta = async ({ label, placement, nextPath }) => {
    // First-party internal tracking
    try {
      await trackEvent({
        eventName: "landing_cta_click",
        page: typeof window !== "undefined" ? window.location.href : "",
        source: "landing",
        customData: { label, placement, nextPath },
      });
    } catch (_) {}

    // Optional Meta CAPI conversion signal (consent gated inside sendMetaEvent)
    try {
      void sendMetaEvent({
        eventName: "Lead",
        eventSourceUrl: typeof window !== "undefined" ? window.location.href : "",
        customData: { label, placement, nextPath, source: "landing" },
      });
    } catch (_) {}

  navigate(nextPath);
};

const handleExternalLandingCta = async ({ label, placement, url }) => {
  // First-party internal tracking
  try {
    await trackEvent({
      eventName: "landing_cta_click",
      page: typeof window !== "undefined" ? window.location.href : "",
      source: "landing",
      customData: { label, placement, url, external: true },
    });
  } catch (_) {}

  // Optional Meta CAPI conversion signal (consent gated inside sendMetaEvent)
  try {
    void sendMetaEvent({
      eventName: "Lead",
      eventSourceUrl: typeof window !== "undefined" ? window.location.href : "",
      customData: { label, placement, url, source: "landing", external: true },
    });
  } catch (_) {}

  try {
    if (typeof window !== "undefined" && url) window.location.href = url;
  } catch (_) {}
};

const handleSocialClick = ({ platform, placement }) => {
    try {
      void trackEvent({
        eventName: 'landing_social_click',
        page: typeof window !== 'undefined' ? window.location.href : '',
        source: 'landing',
        customData: { platform, placement, url: FACEBOOK_PAGE_URL },
      });
    } catch (_) {}
  };

  const goLogin = (nextPath) => navigate(`${createPageUrl('Login')}?next=${encodeURIComponent(nextPath)}`);
  const goRegister = (nextPath) => navigate(`${createPageUrl('Register')}?next=${encodeURIComponent(nextPath)}`);
  const go = (path) => navigate(path);

  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // UX mobile : éviter le scroll de l'arrière-plan quand le menu hamburger est ouvert.
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!mobileMenuOpen) return;

    const prevBody = document.body.style.overflow || '';
    const prevHtml = document.documentElement.style.overflow || '';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [mobileMenuOpen]);

  // Fermer le menu mobile via Échap.
  React.useEffect(() => {
    if (!mobileMenuOpen) return;
    if (typeof window === 'undefined') return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SeoJsonLd id="landing-seo-graph" data={landingSeoGraph} />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <BrandLink to="/" />
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => go(createPageUrl('Tarifs'))}
              data-track="cta"
              data-cta="Tarifs"
              data-placement="header"
            >
              Tarifs
            </Button>
            <Button 
              variant="ghost"
              data-track="cta"
              data-cta="Connexion"
              data-placement="header"
              data-next={createPageUrl('Dashboard')}
              onClick={() => goLogin(createPageUrl('Dashboard'))}
            >
              Connexion
            </Button>
            <Button 
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              data-track="cta"
              data-cta="Commencer"
              data-placement="header"
              data-next={createPageUrl('Onboarding')}
              onClick={() => handleLandingCta({ label: "Commencer", placement: "header", nextPath: `${createPageUrl('Register')}?next=${encodeURIComponent(createPageUrl('Onboarding'))}` })}
            >
              Commencer
            </Button>

            <a
              href={FACEBOOK_PAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook TrésoPilot"
              onClick={() => handleSocialClick({ platform: 'facebook', placement: 'header' })}
              className="inline-flex items-center justify-center w-10 h-10 rounded-md border border-slate-200 text-slate-600 hover:text-emerald-600 hover:border-emerald-200 hover:bg-emerald-50 transition-colors"
            >
              <FacebookIcon className="w-5 h-5" />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label="Ouvrir le menu"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </Button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/50 z-50 md:hidden"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-[92vw] max-w-sm bg-white z-50 shadow-2xl md:hidden flex flex-col max-h-[100svh] overflow-hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <BrandLink to="/" />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Fermer le menu"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <X className="w-6 h-6" />
                </Button>
              </div>
              
              <nav className="p-6 space-y-4 flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y">
                <a
                  href={createPageUrl('Tarifs')}
                  onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); go(createPageUrl('Tarifs')); }}
                  className="block text-lg text-slate-700 hover:text-emerald-600 transition-colors"
                >
                  Tarifs
                </a>
                <a
                  href={createPageUrl('Contact')}
                  onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); go(createPageUrl('Contact')); }}
                  className="block text-lg text-slate-700 hover:text-emerald-600 transition-colors"
                >
                  Contact
                </a>
                
                <div className="pt-4 space-y-3">
                  <Button 
                    variant="outline"
                    className="w-full"
                    data-track="cta"
                    data-cta="Connexion"
                    data-placement="mobile_menu"
                    data-next={createPageUrl('Dashboard')}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      goLogin(createPageUrl('Dashboard'));
                    }}
                  >
                    Connexion
                  </Button>
                  <Button 
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                    data-track="cta"
                    data-cta="Commencer"
                    data-placement="mobile_menu"
                    data-next={createPageUrl('Onboarding')}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleLandingCta({ label: "Commencer", placement: "mobile_menu", nextPath: `${createPageUrl('Register')}?next=${encodeURIComponent(createPageUrl('Onboarding'))}` });
                    }}
                  >
                    Commencer
                  </Button>
                </div>

                <div className="pt-6 border-t border-slate-100">
                  <a
                    href={FACEBOOK_PAGE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleSocialClick({ platform: 'facebook', placement: 'mobile_menu' })}
                    className="inline-flex items-center gap-3 text-slate-700 hover:text-emerald-600 transition-colors"
                  >
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 text-slate-600">
                      <FacebookIcon className="w-5 h-5" />
                    </span>
                    <span className="text-base font-medium">Suivre la page Facebook</span>
                  </a>
                </div>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Hero */}
<section className="pt-32 pb-16 px-4">
  <div className="max-w-5xl mx-auto text-center">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <Badge className="mb-6 bg-emerald-50 text-emerald-700 border-emerald-200">
        <Sparkles className="w-3 h-3 mr-1" />
        Micro-entrepreneurs & indépendants (France)
      </Badge>

      <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
        URSSAF / TVA / Impôts :{" "}
        <span className="bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">
          TrésoPilot
        </span>{" "}
        vous dit combien mettre de côté (automatiquement)
      </h1>

      <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
        Vous saisissez votre <strong>CA encaissé</strong>. TrésoPilot calcule automatiquement les
        <strong> provisions</strong> (URSSAF, impôts, TVA…), vous dit <strong>combien mettre de côté</strong>,
        et vous <strong>rappelle</strong> chaque échéance — tout en préparant vos factures <strong>pour la facture électronique</strong> (Factur‑X prêt).
      </p>

      <div className="mb-8 flex justify-center">
        <div className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2">
          <Tagline className="text-emerald-800" />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button
          asChild
          size="lg"
          className="w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 h-14 px-8 text-lg shadow-lg shadow-emerald-500/20"
        >
          <a href="https://tresopilot.fr/register/">
            Commencer gratuitement
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </Button>

        <Button
          size="lg"
          variant="outline"
          className="w-full sm:w-auto h-14 px-8 text-lg"
          onClick={() => {
            try {
              document.getElementById('how')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } catch (_) {}
          }}
        >
          Voir comment ça marche
        </Button>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-500">
        <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Sans carte bancaire</span>
        <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Sans engagement (annulation en 1 clic)</span>
        <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Configuration guidée</span>
        <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Rappels & exports selon l’offre</span>
        <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Prêt Factur-X (facture électronique)</span>
      </div>
    </motion.div>

    {/* Hero visual */}
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      className="mt-14 relative"
    >
      <div className="bg-white rounded-2xl shadow-2xl shadow-slate-200/50 border border-slate-100 p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-5 text-left">
            <TrendingUp className="w-8 h-8 text-emerald-600 mb-3" />
            <p className="text-sm text-slate-500 mb-1">CA encaissé (mois)</p>
            <p className="text-3xl font-bold text-slate-800">4 250 €</p>
            <p className="text-xs text-slate-500 mt-1">TTC si TVA</p>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-5 text-left">
            <PiggyBank className="w-8 h-8 text-amber-600 mb-3" />
            <p className="text-sm text-slate-500 mb-1">À provisionner</p>
            <p className="text-3xl font-bold text-slate-800">1 105 €</p>
            <p className="text-xs text-slate-500 mt-1">URSSAF + impôts (+ TVA)</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 text-left">
            <Calendar className="w-8 h-8 text-blue-600 mb-3" />
            <p className="text-sm text-slate-500 mb-1">Prochaine échéance</p>
            <p className="text-xl font-bold text-slate-800">31 janv.</p>
            <p className="text-xs text-slate-500">URSSAF (déclaration)</p>
          </div>
        </div>
      </div>
      <div className="absolute -z-10 inset-0 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-3xl blur-3xl" />
    </motion.div>
  </div>
</section>

{/* Demo video (onboarding → 1er CA → résultat) */}
<section id="demo" className="pb-16 px-4">
  <div className="max-w-6xl mx-auto">
    <div className="grid lg:grid-cols-2 gap-8 items-center">
      <div>
        <div className="inline-flex items-center gap-2 mb-3">
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
            <Zap className="w-3 h-3 mr-1" />
            Démo en 60 secondes
          </Badge>
        </div>

        <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
          Regardez exactement ce que vous obtenez
        </h2>

        <p className="mt-3 text-slate-600">
          Vous partez de la landing, vous cliquez sur <strong>« Commencer gratuitement »</strong>, vous faites l'onboarding (5 étapes),
          vous saisissez votre <strong>premier CA encaissé</strong> et TrésoPilot affiche immédiatement :
          <strong> vos cotisations estimées</strong>, <strong>ce qu'il faut mettre de côté</strong> et <strong>ce qu'il vous reste vraiment</strong>.
        </p>

        <ul className="mt-5 space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /> Onboarding guidé (5 étapes) — sans prise de tête</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /> 1er CA → provisions + reste calculés automatiquement</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /> Vous ajoutez un 2e CA et suivez tout dans « Chiffre d'affaires »</li>
        </ul>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Button
            asChild
            className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-lg shadow-emerald-500/15"
            data-track="cta"
            data-cta="Commencer gratuitement"
            data-placement="demo_section"
            data-next={createPageUrl('Onboarding')}
            onClick={(e) => {
              try { e?.preventDefault?.(); } catch (_) {}
              handleLandingCta({ label: "Commencer gratuitement", placement: "demo_section", nextPath: `${createPageUrl('Register')}?next=${encodeURIComponent(createPageUrl('Onboarding'))}` });
            }}
          >
            <a href={`${createPageUrl('Register')}?next=${encodeURIComponent(createPageUrl('Onboarding'))}`}>
              Commencer gratuitement
              <ArrowRight className="w-4 h-4 ml-2" />
            </a>
          </Button>
          <Button
            variant="outline"
            data-track="cta"
            data-cta="Voir les offres"
            data-placement="demo_section"
            onClick={async () => {
              // First-party internal tracking
              try {
                await trackEvent({
                  eventName: "landing_cta_click",
                  page: typeof window !== "undefined" ? window.location.href : "",
                  source: "landing",
                  customData: { label: "Voir les offres", placement: "demo_section", nextPath: "#pricing" },
                });
              } catch (_) {}

              try {
                document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              } catch (_) {}
            }}
          >
            Voir les offres
          </Button>
        </div>

        <p className="mt-3 text-xs text-slate-500">
          Astuce : mettez la vidéo en plein écran — vous verrez le résultat, pas une promesse.
        </p>
      </div>

      <div
        ref={demoVideoShellRef}
        className="relative"
        style={demoSticky && demoStickyPlaceholderH ? { height: demoStickyPlaceholderH } : undefined}
      >
        <div
          ref={demoVideoCardRef}
          className={`bg-white rounded-2xl border border-slate-100 shadow-2xl shadow-slate-200/50 overflow-hidden ${demoSticky ? 'fixed bottom-4 right-4 z-[60] w-[360px] max-w-[92vw] shadow-black/20' : ''}`}
        >
          {demoSticky && (
            <button
              type="button"
              aria-label="Fermer la vidéo"
              className="absolute top-2 right-2 z-[70] rounded-full bg-white/90 backdrop-blur px-2 py-1 text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-white"
              onClick={() => {
                // Pause => le handler "pause" de l'effet s'occupe de fermer le mode mini proprement.
                try { demoVideoRef.current?.pause?.(); } catch (_) {}
              }}
            >
              Fermer
            </button>
          )}
          <video
            ref={demoVideoRef}
            controls
            // Best-effort to discourage downloads / external playback (cannot fully prevent on the web)
            controlsList="nodownload noremoteplayback"
            disableRemotePlayback
            onContextMenu={(e) => { try { e.preventDefault(); } catch (_) {} }}
            preload="metadata"
            playsInline
            className="w-full h-auto"
            poster="/videos/tresopilot-demo-poster.jpg"
          >
            <source src="/videos/tresopilot-presentation-sous-titree-5.mp4" type="video/mp4" />
            Votre navigateur ne supporte pas la vidéo.
          </video>
        </div>
        <div className="absolute -z-10 inset-0 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-3xl blur-3xl" />
      </div>
    </div>
  </div>
</section>

{/* Social proof */}
<section className="pb-10 px-4">
  <div className="max-w-6xl mx-auto">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <Badge className="bg-slate-50 text-slate-700 border-slate-200">Bêta-testeurs</Badge>
          <div className="text-sm text-amber-600 font-semibold">★★★★☆</div>
        </div>
        <p className="text-slate-800 font-semibold mb-2">"Je sais enfin combien mettre de côté."</p>
        <p className="text-slate-600 text-sm">Avant, je naviguais à vue. Là, j’ai mon montant “à provisionner” + mes échéances, sans tableur.</p>
        <p className="text-slate-500 text-xs mt-4">— Micro‑entrepreneure (services)</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <Badge className="bg-slate-50 text-slate-700 border-slate-200">Bêta-testeurs</Badge>
          <div className="text-sm text-amber-600 font-semibold">★★★★★</div>
        </div>
        <p className="text-slate-800 font-semibold mb-2">"Les rappels m’ont sauvé une échéance."</p>
        <p className="text-slate-600 text-sm">Je vois ce qui arrive (URSSAF/TVA) et je peux lisser chaque semaine. Beaucoup moins de stress.</p>
        <p className="text-slate-500 text-xs mt-4">— Indépendant (artisanat)</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <Badge className="bg-slate-50 text-slate-700 border-slate-200">Bêta-testeurs</Badge>
          <div className="text-sm text-amber-600 font-semibold">★★★★★</div>
        </div>
        <p className="text-slate-800 font-semibold mb-2">"Simple et clair, même en TVA."</p>
        <p className="text-slate-600 text-sm">Le TTC encaissé, la TVA, les charges… tout est plus lisible. Je pilote sur la réalité.</p>
        <p className="text-slate-500 text-xs mt-4">— Micro‑entrepreneur (commerce)</p>
      </div>
    </div>

    <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-500">
      <span className="inline-flex items-center gap-2"><Shield className="w-4 h-4" /> Paiement sécurisé (Stripe)</span>
      <span className="inline-flex items-center gap-2"><Zap className="w-4 h-4" /> Résultat en 2 minutes</span>
      <span className="inline-flex items-center gap-2"><Bell className="w-4 h-4" /> Rappels selon l’offre</span>
    </div>
  </div>
</section>

{/* Mini simulateur */}
	<section id="simulateur" className="py-16 px-4 bg-gradient-to-b from-white to-emerald-50/30">
  <div className="max-w-7xl mx-auto">
    <div className="grid lg:grid-cols-2 gap-8 items-start">
      <div>
        <div className="inline-flex items-center gap-2 mb-3">
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
            <Calculator className="w-3 h-3 mr-1" />
            Mini-simulateur
          </Badge>
        </div>

        <h3 className="text-2xl md:text-3xl font-semibold text-slate-900">
          Combien mettre de côté ? (en 10 secondes)
        </h3>
        <p className="text-slate-600 mt-3">
          Un seul chiffre + votre activité… et vous voyez tout de suite l’ordre de grandeur à mettre de côté (et ce qu’il vous reste).
          <span className="block text-xs text-slate-500 mt-2">
            Estimation indicative (hors charges pro, et hors impôt sur le revenu sauf versement libératoire). Dans TrésoPilot, vos taux/échéances sont paramétrables selon votre situation.
          </span>
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => handleExternalLandingCta({ label: "Essai Pro sans CB", placement: "simulateur", url: PRO_TRIAL_NO_CARD_URL })}
          >
            Essai Pro sans CB
            <ArrowRight className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            onClick={() => handleLandingCta({ label: "Tester gratuitement", placement: "simulateur", nextPath: createPageUrl('Register') })}
          >
            Tester gratuitement
          </Button>
          <Button variant="outline" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}>
            Voir les tarifs
          </Button>
        </div>
      </div>

      <Card className="rounded-2xl border-emerald-100 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-slate-900">Votre estimation</CardTitle>
          <CardDescription className="text-slate-600">
            Saisissez votre CA encaissé {simHasTVA ? "(TTC si TVA)" : ""} — {TERRITORY_LABELS[simTerritory] || 'France'}.
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <Label className="text-slate-700">CA encaissé (€)</Label>
                <Input
                  value={simCA}
                  onChange={(e) => setSimCA(e.target.value)}
                  inputMode="decimal"
                  placeholder="ex : 2500"
                  className="mt-2"
                />
              </div>

              <div>
                <Label className="text-slate-700">Territoire fiscal</Label>
                <Select value={simTerritory} onValueChange={setSimTerritory}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Choisir un territoire" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FR">France (métropole)</SelectItem>
                    <SelectItem value="GP">Guadeloupe</SelectItem>
                    <SelectItem value="MQ">Martinique</SelectItem>
                    <SelectItem value="GF">Guyane</SelectItem>
                    <SelectItem value="RE">Réunion</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-xs text-slate-500 mt-1">
                  {simTerritory === 'FR'
                    ? 'Métropole : taux classiques.'
                    : 'DOM : taux URSSAF spécifiques en début d’activité (3 périodes).'}
                </div>
              </div>

              {simTerritory !== 'FR' && (
                <div>
                  <Label className="text-slate-700">Période (DOM)</Label>
                  <Select value={simDomPeriod} onValueChange={setSimDomPeriod}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Choisir une période" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="p1">Période 1 (jusqu’à fin 7e trimestre)</SelectItem>
                      <SelectItem value="p2">Période 2 (jusqu’à fin 3e année)</SelectItem>
                      <SelectItem value="cruise">Régime de croisière (4e année+)</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="text-xs text-slate-500 mt-1">
                    Ces taux sont appliqués automatiquement en DOM (mini-simulateur).
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-slate-700">Activité</Label>
                <Select value={simActivity} onValueChange={setSimActivity}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Choisir votre activité" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vente">Vente / hébergement (URSSAF ~ {previewUrssafPct('vente').toFixed(1)}%)</SelectItem>
                    <SelectItem value="services_bic">Prestations de services (BIC) (URSSAF ~ {previewUrssafPct('services_bic').toFixed(1)}%)</SelectItem>
                    <SelectItem value="liberal_bnc">Activité libérale (BNC) (URSSAF ~ {previewUrssafPct('liberal_bnc').toFixed(1)}%)</SelectItem>
                    <SelectItem value="cipav">Libérale CIPAV (BNC) (URSSAF ~ {previewUrssafPct('cipav').toFixed(1)}%)</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-xs text-slate-500 mt-1">
                  {isDom
                    ? `Taux indicatifs (DOM) — ${DOM_PERIOD_LABELS[simDomPeriod]}.`
                    : "Taux indicatifs (métropole) — vous les ajustez précisément dans TrésoPilot."}
                </div>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-border bg-background px-3 py-2">
                <div>
                  <div className="text-sm font-medium text-slate-900">TVA</div>
                  <div className="text-xs text-slate-500">{simTerritory === 'GF' ? 'Guyane : TVA non applicable' : 'Si vous facturez avec TVA (CA saisi TTC)'}</div>
                </div>
                <Switch checked={simHasTVA} onCheckedChange={setSimHasTVA} disabled={simTerritory === 'GF'} />
              </div>

              {simHasTVA && (
                <div className="flex items-center justify-between rounded-xl border border-border bg-background px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-slate-900">Taux TVA</div>
                    <div className="text-xs text-slate-500">Choisissez le taux appliqué</div>
                  </div>
                  <Select value={simTVARate} onValueChange={setSimTVARate}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue placeholder="20%" />
                    </SelectTrigger>
                    <SelectContent>
                      {simTerritory === 'FR' ? (
                        <>
                          <SelectItem value="0.20">20%</SelectItem>
                          <SelectItem value="0.10">10%</SelectItem>
                          <SelectItem value="0.055">5,5%</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="0.085">8,5%</SelectItem>
                          <SelectItem value="0.021">2,1%</SelectItem>
                          <SelectItem value="0.0105">1,05%</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex items-center justify-between rounded-xl border border-border bg-background px-3 py-2">
                <div>
                  <div className="text-sm font-medium text-slate-900">ACRE</div>
                  <div className="text-xs text-slate-500">{simTerritory === 'FR' ? 'Réduction URSSAF (≈ -50%)' : 'Non appliquée ici (taux DOM spécifiques)'} </div>
                </div>
                <Switch checked={simHasACRE} onCheckedChange={setSimHasACRE} disabled={simTerritory !== 'FR'} />
              </div>

              <div className="flex items-center justify-between rounded-xl border border-border bg-background px-3 py-2">
                <div>
                  <div className="text-sm font-medium text-slate-900">Versement libératoire</div>
                  <div className="text-xs text-slate-500">Ajoute l’IR (1% / 1,7% / 2,2%) si vous l’avez</div>
                </div>
                <Switch checked={simHasVL} onCheckedChange={setSimHasVL} />
              </div>
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
              <div className="text-xs text-emerald-700">À mettre de côté</div>
              <div className="text-2xl font-semibold text-slate-900">
                {Math.round(simTotal).toLocaleString("fr-FR")} €
              </div>
              <div className="text-xs text-slate-500 mt-1">
                URSSAF ~ {urssafPct.toFixed(1)}% ({ACTIVITY_LABELS[simActivity]}){isDom ? ` · ${TERRITORY_LABELS[simTerritory]} · ${DOM_PERIOD_LABELS[simDomPeriod]}` : ''}{simHasVL ? ` · IR (VL) ~ ${vlPct.toFixed(1)}%` : ""}{simHasTVA ? ` · TVA ~ ${tvaPct}%` : ""}
              </div>
            </div>

            <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
              <div className="text-xs text-slate-600">Reste estimé</div>
              <div className="text-2xl font-semibold text-slate-900">
                {Math.round(simRemaining).toLocaleString("fr-FR")} €
              </div>
              <div className="text-xs text-slate-500 mt-1">Après provisions estimées</div>
            </div>

            <div className="rounded-xl bg-white border border-emerald-100 p-4">
              <div className="text-xs text-emerald-700">Rémunération conseillée</div>
              <div className="text-2xl font-semibold text-slate-900">
                {Math.round(simRemuneration).toLocaleString("fr-FR")} €
              </div>
              <div className="text-xs text-slate-500 mt-1">
                En gardant {Math.round(SIM_SAFETY_PCT * 100)}% de coussin (~{Math.round(simSafetyBuffer).toLocaleString("fr-FR")} €)
              </div>
            </div>
          </div>

          {/* CTA séparé (meilleur responsive : évite que les boutons soient “mangés” sur les petits widths) */}
          <div className="mt-4 rounded-xl bg-white border border-border p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs text-slate-600">Prochaine étape</div>
                <div className="text-base font-semibold text-slate-900 mt-1">
                  Automatiser ça dans TrésoPilot
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Taux + échéances précis, rappels, historique, enveloppes… (selon l’offre)
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                <Button
                  className="w-full sm:w-auto whitespace-normal h-auto py-2 leading-snug"
                  onClick={() => handleExternalLandingCta({ label: "Essai Pro sans CB", placement: "simulateur_card", url: PRO_TRIAL_NO_CARD_URL })}
                >
                  Essai Pro sans CB
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  className="w-full sm:w-auto whitespace-normal h-auto py-2 leading-snug"
                  onClick={() => handleLandingCta({ label: "Créer un compte gratuit", placement: "simulateur_card", nextPath: createPageUrl('Register') })}
                >
                  Créer un compte gratuit
                </Button>
              </div>
            </div>
          </div>

        </CardContent>
      </Card>
    </div>
  </div>
</section>

{/* Qui est derrière */}
<section className="py-16 px-4">
  <div className="max-w-7xl mx-auto">
    <div className="grid lg:grid-cols-3 gap-6 items-stretch">
      <Card className="rounded-2xl border-emerald-100 shadow-sm lg:col-span-1">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Logo size="default" showText={false} />
            <div>
              <CardTitle className="text-lg">Qui est derrière TrésoPilot ?</CardTitle>
              <CardDescription>Une histoire simple : moins de stress, plus de clarté.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="text-slate-700">
          <p>
            <span className="font-semibold text-slate-900">Adam</span>, entrepreneur en micro-entreprise.
            Je me suis retrouvé comme tout le monde : <span className="font-medium">URSSAF qui tombe, TVA qui approche…</span>
            et une question : <span className="font-medium">comment arrêter de subir ?</span>
          </p>
          <p className="mt-3 text-slate-600">
            TrésoPilot est né pour transformer le “je verrai plus tard” en un cockpit clair : provisions, échéances et alertes.
          </p>

          <div className="mt-5">
            <Button variant="outline" asChild>
              <Link to={createPageUrl("Contact")}>
                Me contacter
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-border shadow-sm lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-xl text-slate-900">Ce que vous gagnez concrètement</CardTitle>
          <CardDescription className="text-slate-600">
            De la confiance sur vos chiffres — sans vous transformer en comptable.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-5">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold">
                <Shield className="w-4 h-4" />
                Moins de surprises
              </div>
              <p className="text-slate-700 mt-2">
                Vous savez quoi mettre de côté et quand — avant que ça pique.
              </p>
            </div>

            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-5">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold">
                <Zap className="w-4 h-4" />
                Moins de friction
              </div>
              <p className="text-slate-700 mt-2">
                Un onboarding rapide, puis un dashboard qui reste simple à lire.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <div className="flex items-center gap-2 text-slate-800 font-semibold">
                <Bell className="w-4 h-4" />
                Rappels & échéances
              </div>
              <p className="text-slate-700 mt-2">
                Alertes in-app + email (selon l’offre) pour ne rien oublier.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <div className="flex items-center gap-2 text-slate-800 font-semibold">
                <PiggyBank className="w-4 h-4" />
                Provisions automatiques
              </div>
              <p className="text-slate-700 mt-2">
                TrésoPilot calcule et suit vos provisions selon vos réglages.
              </p>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-border bg-background p-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <div className="font-semibold text-slate-900">Nouveau sur le marché ? Oui — et transparent.</div>
                <div className="text-sm text-slate-600 mt-1">
                  Vous testez. Vous gardez le contrôle. Annulation en 1 clic.
                </div>
              </div>
              <Button variant="outline" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}>
                Voir le prix
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
</section>

{/* How it works */}
<section className="pb-16 px-4" id="how">
  <div className="max-w-6xl mx-auto">
    <div className="text-center mb-10">
      <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
        Simple : vous ajoutez votre CA, TrésoPilot pilote le reste
      </h2>
      <p className="text-slate-600 text-lg">
        En 3 étapes : vous voyez ce que vous avez, ce que vous devez mettre de côté, et ce qui arrive bientôt.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-emerald-100 text-emerald-700 rounded-xl px-3 py-1 text-sm font-semibold">1</div>
          <p className="font-semibold text-slate-800">Vous saisissez votre CA encaissé</p>
        </div>
        <p className="text-slate-600">En 10 secondes (mensuel ou quotidien). TrésoPilot suit votre CA TTC si TVA.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-emerald-100 text-emerald-700 rounded-xl px-3 py-1 text-sm font-semibold">2</div>
          <p className="font-semibold text-slate-800">Le “vrai net” se calcule</p>
        </div>
        <p className="text-slate-600">Provisions automatiques URSSAF/impôts/TVA + enveloppes virtuelles selon l’offre.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-emerald-100 text-emerald-700 rounded-xl px-3 py-1 text-sm font-semibold">3</div>
          <p className="font-semibold text-slate-800">Vous n’oubliez plus rien</p>
        </div>
        <p className="text-slate-600">Échéancier + rappels (in-app & email). Exports PDF/CSV + Factur-X prêt pour vos factures (selon l’offre).</p>
      </div>
    </div>
  </div>
</section>

      {/* Product tour */}
      <section className="py-20 px-4 bg-gradient-to-b from-white to-slate-50" id="apercu">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 mb-4">Aperçu</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Le cockpit TrésoPilot, en images
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Pas de promesses floues : voici exactement ce que vous verrez dans votre compte après inscription.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6 md:p-8 shadow-sm mb-14">
            <div className="flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
              <div className="flex-1">
                <div className="flex items-start gap-3">
                  <div className="bg-amber-50 border border-amber-100 text-amber-700 rounded-xl p-2">
                    <AlertCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-2">
                      Le piège classique : confondre “CA” et “argent dispo”
                    </h3>
                    <p className="text-slate-600">
                      Beaucoup se paient trop tôt, puis subissent la double peine : <strong>URSSAF / TVA</strong> qui tombe, et trésorerie qui s'effondre.
                      TrésoPilot vous redonne une règle simple : <strong>voir</strong>, <strong>provisionner</strong>, <strong>anticiper</strong>.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <a href="/register">Créer mon compte gratuit</a>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                  <Link to={TRIAL_FLOW_URL}>Essai Pro 7 jours</Link>
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-16">
            {productTour.map((item, index) => (
              <motion.div
                key={item.kicker}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center"
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1 mb-4">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    {item.kicker}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600 text-lg mb-6">{item.description}</p>
                  <ul className="space-y-3 mb-7">
                    {item.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-slate-700">
                        <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button asChild size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Link to={item.ctaPrimaryHref || TRIAL_FLOW_URL}>{item.ctaPrimaryLabel || 'Commencer maintenant'}</Link>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                      <Link to={item.ctaSecondaryHref || TRIAL_FLOW_URL}>{item.ctaSecondaryLabel || 'Essai Pro 7 jours'}</Link>
                    </Button>
                  </div>

                  <p className="text-sm text-slate-500 mt-3">
                    {item.urgency}
                  </p>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="relative rounded-3xl border border-slate-200 shadow-sm bg-white overflow-hidden">
                    <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-transparent to-white/30" />
                    <img
                      src={item.image}
                      alt={item.alt}
                      loading="lazy"
                      className="w-full h-auto block"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Tout est pensé pour être clair
            </h2>
            <p className="text-lg text-slate-600">
              Dashboard, échéances, provisions… vous savez quoi faire et quand le faire.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-lg hover:shadow-slate-200/50 transition-all"
              >
                <div className="bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl p-3 w-fit mb-4">
                  <feature.icon className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pack Tranquillité */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl p-8 md:p-12 text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '32px 32px'
              }} />
            </div>
            <div className="relative z-10">
              <div className="flex flex-col md:flex-row items-start justify-between gap-6 mb-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <Shield className="w-10 h-10" />
                    <h3 className="text-3xl font-bold">Pack Tranquillité 60 jours</h3>
                  </div>
                  <p className="text-purple-100 text-lg mb-6">
                    Testez toutes les fonctionnalités Plus pendant 2 mois, sans engagement
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    <div className="flex items-center gap-3 text-purple-100">
                      <div className="bg-white/20 rounded-lg p-2">
                        <Check className="w-5 h-5" />
                      </div>
                      <span>Historique illimité</span>
                    </div>
                    <div className="flex items-center gap-3 text-purple-100">
                      <div className="bg-white/20 rounded-lg p-2">
                        <Check className="w-5 h-5" />
                      </div>
                      <span>Enveloppes virtuelles</span>
                    </div>
                    <div className="flex items-center gap-3 text-purple-100">
                      <div className="bg-white/20 rounded-lg p-2">
                        <Check className="w-5 h-5" />
                      </div>
                      <span>Rappels email</span>
                    </div>
                    <div className="flex items-center gap-3 text-purple-100">
                      <div className="bg-white/20 rounded-lg p-2">
                        <Check className="w-5 h-5" />
                      </div>
                      <span>Exports PDF & CSV</span>
                    </div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                    <p className="text-sm text-purple-100 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span><strong>Offre unique :</strong> Une seule fois par compte, sans abonnement récurrent</span>
                    </p>
                  </div>
                </div>
                <div className="text-center md:text-right min-w-[200px]">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 mb-4">
                    <div className="text-6xl md:text-7xl font-bold mb-2">9,99€</div>
                    <div className="text-purple-200 text-lg">Paiement unique</div>
                    <div className="text-sm text-purple-300 mt-2">Soit moins de 5€/mois sur 2 mois</div>
                  </div>
                  <Button
                    size="lg"
                    className="w-full bg-white text-purple-600 hover:bg-purple-50 h-14 text-lg font-bold shadow-xl"
                    data-track="cta"
                    data-cta="Acheter le Pack"
                    data-placement="pricing_pack_banner"
                    data-next={createPageUrl('Billing')}
                    onClick={() => handleLandingCta({ label: "Acheter le Pack", placement: "pricing_pack_banner", nextPath: `${createPageUrl('Register')}?next=${encodeURIComponent(createPageUrl('Billing'))}` })}
                  >
                    <Shield className="w-5 h-5 mr-2" />
                    Acheter le Pack
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Facture électronique */}
      <section className="py-14 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl border border-slate-100 p-8 md:p-10 shadow-sm">
            <div className="flex flex-col lg:flex-row gap-8 lg:items-center lg:justify-between">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1 mb-4">
                  <Sparkles className="w-4 h-4" />
                  Facture électronique (2026/2027)
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                  Factur‑X ready : vous êtes prêt sans vous prendre la tête
                </h2>
                <p className="text-slate-600 text-lg mb-6">
                  TrésoPilot prépare vos factures pour être compatibles avec la facture électronique (format Factur‑X / EN16931),
                  pour éviter un nouveau chantier administratif au dernier moment.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-slate-800 font-semibold mb-1">Compatibilité</p>
                    <p className="text-sm text-slate-600">Factures structurées & exportables (selon l’offre).</p>
                  </div>
                  <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-slate-800 font-semibold mb-1">Moins de stress</p>
                    <p className="text-sm text-slate-600">Vous anticipez dès maintenant, sans refaire vos process.</p>
                  </div>
                  <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-slate-800 font-semibold mb-1">Un cockpit unique</p>
                    <p className="text-sm text-slate-600">Trésorerie + échéances + factures au même endroit.</p>
                  </div>
                </div>
              </div>

              <div className="min-w-[260px]">
                <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 p-6 text-white shadow-xl shadow-emerald-500/20">
                  <div className="text-sm opacity-90 mb-2">Bonus inclus</div>
                  <div className="text-2xl font-bold mb-2">Factur‑X prêt</div>
                  <p className="text-white/90 text-sm mb-5">
                    Disponible selon l’offre. Idéal pour rester serein face aux changements.
                  </p>
                  <Button asChild size="lg" className="w-full bg-white text-emerald-700 hover:bg-emerald-50">
                    <a href="/register">Créer mon compte</a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

{/* Social Proof Carousel */}
<section className="py-12 bg-gray-50 overflow-hidden">
  <div className="max-w-7xl mx-auto px-4">
    <div className="flex items-end justify-between gap-4 mb-6">
      <h3 className="text-xl font-semibold">Ils en parlent</h3>
      
    </div>

    {(() => {
      const SOCIAL_PROOF = [
        { text: "Je sais enfin combien mettre de côté.", rating: 5, name: "Camille", activity: "Webdesigner", city: "Lyon (69)" },
        { text: "Fini les surprises URSSAF.", rating: 5, name: "Mehdi", activity: "Artisan peintre", city: "Marseille (13)" },
        { text: "Beaucoup plus clair que mon Excel.", rating: 4, name: "Sarah", activity: "Coach", city: "Toulouse (31)" },

        // Un avis plus nuancé (3/5) pour crédibilité
        { text: "Bonne base, j'attends encore 1–2 options en plus.", rating: 3, name: "Nicolas", activity: "Auto-entrepreneur", city: "Clermont-Ferrand (63)" },

        { text: "Les rappels m'ont évité un oubli.", rating: 5, name: "Julien", activity: "Consultant IT", city: "Paris (75)" },

        // Un avis plus nuancé (3/5) pour crédibilité
        { text: "Utile, mais il m'a fallu un moment pour tout paramétrer.", rating: 3, name: "Mélanie", activity: "Artisane", city: "Reims (51)" },

        { text: "J'avais toujours peur de la TVA.", rating: 4, name: "Inès", activity: "Graphiste", city: "Bordeaux (33)" },
        { text: "Configuration hyper rapide.", rating: 5, name: "Thomas", activity: "Électricien", city: "Lille (59)" },
        { text: "Je vois mon vrai reste à vivre.", rating: 5, name: "Élodie", activity: "Secrétaire médicale", city: "Nantes (44)" },

        // Bêta-testeur 4/5 comme demandé
        { text: "Très prometteur, ça m'aide déjà à anticiper.", rating: 4, name: "Karim", activity: "Plombier", city: "Strasbourg (67)", badge: "Bêta-testeurs" },

        { text: "Tout est clair dès le premier jour.", rating: 5, name: "Manon", activity: "Photographe", city: "Montpellier (34)" },
        { text: "Plus besoin de tableaux compliqués.", rating: 4, name: "Nadia", activity: "Community manager", city: "Rennes (35)" },

        // Un avis plus nuancé (3/5) pour crédibilité
        { text: "Ça fait le job, j'aimerais plus de personnalisation.", rating: 3, name: "Sofiane", activity: "Coach sportif", city: "Nice (06)" },

        { text: "Je n'oublie plus aucune échéance.", rating: 5, name: "Hugo", activity: "Formateur", city: "Grenoble (38)" },
        { text: "Ça enlève une grosse charge mentale.", rating: 5, name: "Laura", activity: "Coiffeuse à domicile", city: "Tours (37)" },
        { text: "Enfin un outil pensé micro-entrepreneur.", rating: 5, name: "Lucas", activity: "Paysagiste", city: "Rouen (76)" },
      ];

      const starsFor = (rating) => "★★★★★".slice(0, rating) + "☆☆☆☆☆".slice(0, 5 - rating);

      return (
        <div className="marquee">
          <div className="marquee__track">
            {[...SOCIAL_PROOF, ...SOCIAL_PROOF].map((item, i) => (
              <div
                key={i}
                className="min-w-[280px] bg-white rounded-xl shadow-sm border border-gray-100 px-4 py-3 text-sm text-gray-800"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs tracking-wide text-gray-500">{starsFor(item.rating)}</span>
                  <span className="text-xs text-gray-400">{item.rating}/5</span>
                </div>
                <div className="text-sm leading-snug">“{item.text}”</div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500"><span>— {item.name} · {item.activity} · {item.city}</span>{item.badge ? (<span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">{item.badge}</span>) : null}</div>
              </div>
            ))}
          </div>
        </div>
      );
    })()}
  </div>
</section>

<style>{`
.marquee { overflow: hidden; }
.marquee__track {
  display: flex;
  gap: 1.25rem;
  width: max-content;
  animation: marquee 95s linear infinite;
  will-change: transform;
}
.marquee:hover .marquee__track { animation-play-state: paused; }
@keyframes marquee {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
@media (prefers-reduced-motion: reduce) {
  .marquee__track { animation: none; }
}
`}</style>

{/* UGC video */}
<section className="py-14 px-4 bg-white">
  <div className="max-w-6xl mx-auto">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
      <div>
        <div className="inline-flex items-center gap-2 text-sm font-semibold text-purple-700 bg-purple-50 border border-purple-100 rounded-full px-3 py-1 mb-4">
          <Users className="w-4 h-4" />
          Preuve réelle (UGC)
        </div>
        <h3 className="text-2xl md:text-3xl font-semibold text-slate-900">
          Une ambassadrice partage son retour d’expérience
        </h3>
        <p className="text-slate-600 mt-3 max-w-xl">
          En moins d’une minute, elle explique ce que TrésoPilot a changé pour elle : provisions (URSSAF/TVA/impôts), échéances, et vision claire du “vrai net”.
        </p>
        <ul className="mt-5 space-y-2 text-sm text-slate-700">
          <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5" /><span>Une vision claire, sans tableur</span></li>
          <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5" /><span>Échéances + rappels selon l’offre</span></li>
          <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5" /><span>Devis/contrats acceptés en ligne (preuve)</span></li>
        </ul>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Button onClick={() => handleLandingCta({ label: 'Démarrer l’essai Pro', placement: 'ugc_video', nextPath: TRIAL_FLOW_URL })}>
            Démarrer l’essai Pro
            <ArrowRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={() => handleLandingCta({ label: 'Voir les tarifs', placement: 'ugc_video', nextPath: createPageUrl('Tarifs') })}>
            Voir les tarifs
          </Button>
        </div>
      </div>

      <div className="flex justify-center lg:justify-end">
        <UgcFacebookReel />
      </div>
    </div>
  </div>
</section>

{/* Comparatif */}
<section className="py-16 px-4" id="comparatif">
  <div className="max-w-7xl mx-auto">
    <div className="flex items-start justify-between gap-4 flex-col md:flex-row">
      <div>
        <h3 className="text-2xl md:text-3xl font-semibold text-slate-900">
          Pourquoi TrésoPilot plutôt qu’Excel ou un logiciel compta ?
        </h3>
        <p className="text-slate-600 mt-3 max-w-3xl">
          TrésoPilot n’essaie pas de “tout faire”. Il fait l’essentiel pour la micro-entreprise :
          <span className="font-medium"> provisions + échéances + alertes</span>.
        </p>
      </div>

      <Button variant="outline" onClick={() => handleLandingCta({ label: "Tester en 2 minutes", placement: "comparatif", nextPath: createPageUrl('Register') })}>
        Tester en 2 minutes
      </Button>
    </div>

    <div className="grid lg:grid-cols-3 gap-4 mt-8">
      <Card className="rounded-2xl shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Excel</CardTitle>
          <CardDescription>Simple au début, fragile dans le temps.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-slate-700">
          <div className="flex gap-2"><XCircle className="w-4 h-4 text-rose-500 mt-0.5" /><span>Calculs manuels (erreurs fréquentes)</span></div>
          <div className="flex gap-2"><XCircle className="w-4 h-4 text-rose-500 mt-0.5" /><span>Pas d’échéancier automatique</span></div>
          <div className="flex gap-2"><XCircle className="w-4 h-4 text-rose-500 mt-0.5" /><span>Pas d’alertes seuil TVA</span></div>
          <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /><span>Gratuit</span></div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Logiciel compta</CardTitle>
          <CardDescription>Très complet, parfois trop pour la micro.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-slate-700">
          <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /><span>Factures / devis / exports</span></div>
          <div className="flex gap-2"><AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" /><span>Souvent “compta-first”</span></div>
          <div className="flex gap-2"><AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" /><span>Provisions & échéances pas toujours simples</span></div>
          <div className="flex gap-2"><AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" /><span>Courbe d’apprentissage</span></div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl shadow-sm border-emerald-200 bg-emerald-50/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">TrésoPilot</CardTitle>
            <Badge className="bg-white text-emerald-700 border-emerald-200">Micro-friendly</Badge>
          </div>
          <CardDescription>Le cockpit anti-surprise.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-slate-800">
          <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /><span>“À mettre de côté” automatique</span></div>
          <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /><span>Échéancier clair + rappels (selon offre)</span></div>
          <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /><span>Alertes seuil TVA</span></div>
          <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /><span>Prêt Factur-X (e-facture)</span></div>

          <Button className="w-full mt-4" onClick={() => handleLandingCta({ label: "Commencer gratuitement", placement: "comparatif", nextPath: createPageUrl('Register') })}>
            Commencer gratuitement
            <ArrowRight className="w-4 h-4" />
          </Button>
        </CardContent>
      </Card>
    </div>
  </div>
</section>

{/* Pricing */}
      <section className="py-20 px-4" id="pricing">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Tarifs simples et transparents
            </h2>
            <p className="text-lg text-slate-600">
              Commencez gratuitement, évoluez quand vous voulez
            </p>
<div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-500">
  <span className="inline-flex items-center gap-2"><Shield className="w-4 h-4 text-emerald-600" /> Paiement sécurisé via Stripe</span>
  <span className="inline-flex items-center gap-2"><Shield className="w-4 h-4 text-emerald-600" /> Aucun accès à vos comptes bancaires</span>
  <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Annulation en 1 clic</span>
</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-white rounded-2xl p-6 border flex flex-col ${
                  plan.popular 
                    ? 'border-emerald-200 shadow-xl shadow-emerald-500/10 ring-2 ring-emerald-500' 
                    : 'border-slate-200'
                }`}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white border-0">
                    Populaire
                  </Badge>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-slate-800 mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-slate-900">
                      {plan.price.monthly === 0
                        ? 'Gratuit'
                        : `${formatPriceFr(plan.price.monthly)}€/mois`}
                    </span>
                  </div>
                  {plan.price.yearly > 0 && (
                    <p className="text-sm text-emerald-600 mt-1">ou {formatPriceFr(plan.price.yearly)}€/an</p>
                  )}
                  <p className="text-sm text-slate-500 mt-2">{plan.description}</p>
                </div>
                <ul className="space-y-3 mb-6 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-slate-600">{feature}</span>
                    </li>
                  ))}
                  {plan.limitations?.map((limitation) => (
                    <li key={limitation} className="flex items-start gap-2 opacity-40">
                      <span className="w-5 h-5 flex-shrink-0 text-center text-slate-400">✕</span>
                      <span className="text-sm text-slate-500 line-through">{limitation}</span>
                    </li>
                  ))}
                </ul>

                {plan.id !== 'free' && (
                  <p className="text-xs text-center text-slate-500 mb-3">
                    <strong className="text-slate-700">Sans engagement</strong> - Résiliez quand vous voulez
                  </p>
                )}

                <Button 
                  className={`w-full ${
                    plan.popular 
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700' 
                      : ''
                  }`}
                  variant={plan.variant}
                  data-track="cta"
                  data-cta={plan.cta}
                  data-placement="pricing_card"
                  data-next={createPageUrl('Billing')}
                  data-plan={plan.id}
                  onClick={() => handleLandingCta({ label: plan.cta, placement: "pricing_card", nextPath: `${createPageUrl('Register')}?next=${encodeURIComponent(plan.id === 'free' ? createPageUrl('Onboarding') : createPageUrl('Billing'))}` })}
                >
                  {plan.cta}
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-50 text-purple-700 border-purple-200">
              Questions fréquentes
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Tout ce que vous devez savoir
            </h2>
            <p className="text-lg text-slate-600">
              Les réponses aux questions les plus courantes
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-slate-50 rounded-2xl p-6 md:p-8"
          >
            <Accordion type="single" collapsible className="space-y-3">
              <AccordionItem value="faq-1" className="bg-white rounded-xl px-4 border-0">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">
                  TrésoPilot est-il conforme URSSAF/TVA ?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700 leading-relaxed">
                  TrésoPilot est un outil d'aide à la gestion qui fournit des estimations et rappels. 
                  Vous restez responsable de vos déclarations officielles. Les calculs sont basés sur vos paramètres, 
                  mais vérifie toujours sur les portails officiels (URSSAF, impôts.gouv.fr).
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-2" className="bg-white rounded-xl px-4 border-0">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">
                  Pourquoi payer si je peux utiliser Excel ?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700 leading-relaxed">
                  Avec Excel, vous devez tout calculer manuellement et risques d'oublier une échéance. 
                  TrésoPilot automatise les calculs, envoie des rappels, garde votre historique complet, 
                  et génère des exports pour votre comptable. C'est comme avoir un assistant financier 
                  pour quelques euros par mois. Fini la charge mentale !
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-3" className="bg-white rounded-xl px-4 border-0">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">
                  Qu'est-ce que j'ai exactement dans Plus / Pro ?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700 leading-relaxed">
                  <strong>Plus (6,99€/mois)</strong> : historique illimité, enveloppes virtuelles, rappels email, 
                  exports PDF/CSV. <strong>Pro (12,99€/mois)</strong> : tout de Plus + multi-activités, 
                  alertes TVA, simulations avancées, partage comptable.
                </AccordionContent>
              </AccordionItem>

	              <AccordionItem value="faq-facturx" className="bg-white rounded-xl px-4 border-0">
	                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">
	                  TrésoPilot est-il prêt pour la facture électronique (Factur-X) ?
	                </AccordionTrigger>
	                <AccordionContent className="text-slate-700 leading-relaxed">
	                  Oui. TrésoPilot vous guide sur les <strong>champs obligatoires</strong> (vendeur + client), affiche un badge
	                  <strong>“Champs manquants”</strong> sur chaque facture, et prépare votre <strong>Factur-X (EN16931)</strong> pour l’export.
	                  L’envoi automatique via une <strong>PDP</strong> arrive ensuite (Phase B) : vous restez déjà “prêt” sans changer votre process.
	                </AccordionContent>
	              </AccordionItem>

              <AccordionItem value="faq-4" className="bg-white rounded-xl px-4 border-0">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">
                  Le paiement est-il sécurisé ?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700 leading-relaxed">
                  Oui ! Tous les paiements sont gérés par Stripe, leader mondial du paiement en ligne. 
                  Vos données bancaires ne transitent jamais par nos serveurs et ne sont jamais stockées 
                  par TrésoPilot. Stripe est certifié PCI-DSS niveau 1 (plus haut niveau de sécurité).
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-5" className="bg-white rounded-xl px-4 border-0">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">
                  Puis-je annuler mon abonnement quand je veux ?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700 leading-relaxed">
                  Oui, vous pouvez annuler à tout moment sans frais. Va dans Abonnement → "Gérer mon abonnement" 
                  pour résilier en un clic. Votre abonnement reste actif jusqu'à la fin de la période payée, 
                  puis ne sera pas renouvelé. Vous gardez l'accès à vos données (en mode gratuit).
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-6" className="bg-white rounded-xl px-4 border-0">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">
                  Est-ce que vous m'envoyez des emails ?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700 leading-relaxed">
                  Uniquement des emails transactionnels (confirmation d'inscription, paiement) et des rappels 
                  d'échéances si vous les actives. Nous n'envoyons JAMAIS d'emails marketing ou spam. 
                  Vous pouvez désactiver les rappels dans les paramètres à tout moment.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-7" className="bg-white rounded-xl px-4 border-0">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">
                  Ça prend combien de temps à configurer ?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700 leading-relaxed">
                  2 minutes chrono ! L'onboarding se fait en 4 étapes : fréquence de déclaration, 
                  type d'activité, options fiscales, confirmation. Ensuite vous ajoutez votre premier CA 
                  et c'est parti. TrésoPilot calcule automatiquement vos cotisations.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-8" className="bg-white rounded-xl px-4 border-0">
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline">
                  Je débute et j'ai 0€ de CA, ça sert à quelque chose ?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700 leading-relaxed">
                  Oui ! Même avec 0€ de CA, TrésoPilot vous permet de préparer vos échéances futures, 
                  configurer vos taux correctement dès le départ, et comprendre combien vous devrez 
                  mettre de côté dès votre premier euro. C'est le meilleur moment pour démarrer !
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className="mt-8 text-center">
              <a
                href={createPageUrl('FAQ')}
                className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold"
               onClick={(e) => { e.preventDefault(); go(createPageUrl('FAQ')); }}>
                Voir toute la FAQ
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Guides SEO / contenu utile */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mb-8">
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 mb-4">Guides utiles</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Des ressources utiles pour mieux piloter votre activité</h2>
            <p className="text-lg text-slate-600">
              Nous avons ajouté des guides pratiques pour répondre aux recherches les plus fréquentes des indépendants : trésorerie, rémunération, URSSAF, TVA et alternatives aux outils connus.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {PROGRAMMATIC_PAGES.slice(0, 8).map((page) => (
              <a
                key={page.path}
                href={page.path}
                className="group rounded-2xl border border-slate-200 bg-slate-50 p-5 hover:border-emerald-300 hover:bg-white hover:shadow-sm transition-all"
                onClick={(e) => { e.preventDefault(); go(page.path); }}
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-2">{page.heroBadge}</p>
                <h3 className="font-semibold text-slate-900 leading-snug group-hover:text-emerald-700 transition-colors">
                  {page.heroTitle}
                </h3>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{page.description}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-emerald-500 to-teal-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Prêt à piloter votre activité ?
          </h2>
          <p className="text-lg text-emerald-100 mb-8">
            Rejoins des milliers de micro-entrepreneurs qui gèrent leurs finances sereinement
          </p>
          <Button 
            size="lg" 
            className="bg-white text-emerald-600 hover:bg-slate-100 h-14 px-8 text-lg shadow-lg"
            data-track="cta"
            data-cta="Commencer maintenant"
            data-placement="bottom_cta"
            data-next={createPageUrl('Onboarding')}
            onClick={() => handleLandingCta({ label: "Commencer maintenant", placement: "bottom_cta", nextPath: `${createPageUrl('Register')}?next=${encodeURIComponent(createPageUrl('Onboarding'))}` })}
          >
            Commencer gratuitement
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-center gap-6 mb-8">
            <BrandLink to="/" variant="light" />

            <div className="flex items-center justify-center gap-3">
              <a
                href={FACEBOOK_PAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook TrésoPilot"
                onClick={() => handleSocialClick({ platform: 'facebook', placement: 'footer' })}
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-white transition-colors"
              >
                <FacebookIcon className="w-5 h-5" />
              </a>
              <span className="text-slate-300 text-sm">Rejoindre la communauté TrésoPilot sur Facebook</span>
            </div>

            {/* Contact Section */}
            <div className="bg-slate-800 rounded-xl p-4 max-w-md">
              <p className="text-slate-300 font-semibold mb-3 text-center">Contact</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-center gap-2">
                  <span className="text-slate-400">Email :</span>
                  <a href="mailto:contact@tresopilot.fr" className="text-emerald-400 hover:text-emerald-300">
                    contact@tresopilot.fr
                  </a>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-slate-400">WhatsApp :</span>
                  <a href="https://wa.me/33756901580" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300">
                    +33 7 56 90 15 80
                  </a>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-3 text-sm">
              <a href={createPageUrl('FAQ')} className="text-slate-400 hover:text-white transition-colors" onClick={(e) => { e.preventDefault(); go(createPageUrl('FAQ')); }}>
                FAQ
              </a>
              <span className="text-slate-600">•</span>
              <a href={createPageUrl('Contact')} className="text-slate-400 hover:text-white transition-colors" onClick={(e) => { e.preventDefault(); go(createPageUrl('Contact')); }}>
                Contact
              </a>
              <span className="text-slate-600">•</span>
              <a href={createPageUrl('MentionsLegales')} className="text-slate-400 hover:text-white transition-colors" onClick={(e) => { e.preventDefault(); go(createPageUrl('MentionsLegales')); }}>
                Mentions légales
              </a>
              <span className="text-slate-600">•</span>
              <a href={createPageUrl('CGU')} className="text-slate-400 hover:text-white transition-colors" onClick={(e) => { e.preventDefault(); go(createPageUrl('CGU')); }}>
                CGU
              </a>
              <span className="text-slate-600">•</span>
              <a href={createPageUrl('Confidentialite')} className="text-slate-400 hover:text-white transition-colors" onClick={(e) => { e.preventDefault(); go(createPageUrl('Confidentialite')); }}>
                Politique de confidentialité
              </a>
              <span className="text-slate-600">•</span>
              <a href={createPageUrl('Cookies')} className="text-slate-400 hover:text-white transition-colors" onClick={(e) => { e.preventDefault(); go(createPageUrl('Cookies')); }}>
                Cookies
              </a>
              <span className="text-slate-600">•</span>
              <a href={createPageUrl('Disclaimer')} className="text-slate-400 hover:text-white transition-colors" onClick={(e) => { e.preventDefault(); go(createPageUrl('Disclaimer')); }}>
                Avertissement
              </a>
              <span className="text-slate-600">•</span>
              <CookieSettingsLink />
            </div>
            <div className="bg-slate-800 rounded-xl p-4 max-w-5xl">
              <p className="text-slate-300 font-semibold mb-4 text-center">Guides utiles</p>
              <div className="flex flex-wrap justify-center gap-3 text-sm">
                {PROGRAMMATIC_PAGES.slice(0, 8).map((page) => (
                  <a
                    key={page.path}
                    href={page.path}
                    className="text-slate-400 hover:text-white transition-colors"
                    onClick={(e) => { e.preventDefault(); go(page.path); }}
                  >
                    {page.heroTitle}
                  </a>
                ))}
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-4 max-w-3xl text-center">
              <p className="text-slate-400 text-sm leading-relaxed">
                <strong className="text-slate-300">Avertissement important :</strong> TrésoPilot n'est pas un service de comptabilité ou d'expertise-comptable. 
                Les montants, dates et estimations sont fournis à titre indicatif selon les informations que vous renseignez. 
                Vérifiez toujours vos obligations fiscales et sociales sur les portails officiels (URSSAF, impots.gouv.fr).
              </p>
            </div>
            <p className="text-emerald-200 text-sm">
              <Tagline className="text-emerald-200" />
            </p>
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} TrésoPilot. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}